/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useState } from 'react';

import { Box } from '@mui/material';
import { useNotes } from 'src/florence/hooks/useNotes';

import { usePrefetch } from '../../../common/apis/FlorenceApi';
import { SaveSheet, SheetStatus } from '../../../common/types/SaveSheet';
import SnackbarUtils from '../../../common/utils/SnackbarUtilsConfigurator';
import AIOutputSection from '../../components/SearchResultsContainerV2/componentsV2/AIOutputSection';
import CodesSection from '../../components/SearchResultsContainerV2/componentsV2/CodesSection';
import OpNoteHeader from '../../components/SearchResultsContainerV2/componentsV2/OpNoteHeader';
import { useCodesExtended } from '../../hooks/useCodesExtended';

import CCIAlertBottomBar from './CCIAlertBottomBar';

type Props = {
  notes: SaveSheet[];
};

const EditOpNoteExtendedSurgery: React.FC<Props> = ({ notes }) => {
  // filters
  const [appHeaderHeight, setAppHeaderHeight] = useState(0);
  const [opNoteHeaderHeight, setOpNoteHeaderHeight] = useState(0);

  // ui
  const [isSaving, setIsSaving] = React.useState(false);
  const [shouldFinalize, setShouldFinalize] = useState(false);

  // misc
  const getModifiers = usePrefetch('getModifiers');

  // note and codes
  const {
    currentNote,
    finalizeNote,
    updateNote,
    updateNoteTextWithoutDeepCopy,
    page,
    setPage,
  } = useNotes(notes);

  const {
    codes,
    selectedCodes,
    isLoading,
    selectModifiers,
    selectMultiplier,
    refetchCodes,
    selectCode,
    selectAllCodes,
    clearSelectedCodes,
    addManualCode,
    incorrectCodeCombinations,
    flagCode,
    AIOutput,
  } = useCodesExtended(currentNote, currentNote?.scanText ?? '', 'scan', 20);

  // Run on page load
  React.useEffect(() => {
    getModifiers();

    // If note doesn't have any codes, refetch codes
    if (!currentNote?.sheetCodes?.length) {
      refetchCodes();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedAll = (select: boolean) => {
    if (select) selectAllCodes();
    else clearSelectedCodes();
  };

  const onSaveNote = async () => {
    if (!selectedCodes.length) {
      return SnackbarUtils.error(
        'You need to have at least one code selected.'
      );
    }

    // Set the flag to finalize after the next codes update
    setShouldFinalize(true);
    refetchCodes();
  };

  useEffect(() => {
    const calculateCombinedHeight = () => {
      const opNoteHeader = document.getElementById('op-note-header');
      const opNoteHeaderHeight = opNoteHeader ? opNoteHeader.offsetHeight : 0;

      setOpNoteHeaderHeight(opNoteHeaderHeight + 20);

      const appHeader = document.getElementsByTagName('header')[0];
      const appHeaderHeight = appHeader ? appHeader.offsetHeight : 0;
      setAppHeaderHeight(appHeaderHeight);
    };

    // Calculate combined height on initial render
    calculateCombinedHeight();

    // Recalculate combined height whenever the window is resized
    window.addEventListener('resize', calculateCombinedHeight);

    // Clean up event listener on unmount
    return () => {
      window.removeEventListener('resize', calculateCombinedHeight);
    };
  }, [selectedCodes]);

  useEffect(() => {
    // Auto save whenever AI Output changes (between layers for example)
    // Only finalize the note when Check Codes was clicked
    if (!currentNote || AIOutput === null) return;

    // Don't save if we're still loading Layer 2 results
    if (isLoading) return;

    const autoSaveAndFinalize = async () => {
      const opNote = {
        ...currentNote,
        sheetCodes: selectedCodes,
        aiOutput: AIOutput,
      };

      await updateNote(opNote as SaveSheet, false);

      // Only finalize if the "Check Codes" button was clicked
      if (shouldFinalize && opNote.status !== SheetStatus.FINALIZED) {
        await finalizeNote(opNote as SaveSheet, false);
        // Reset the flag after finalizing
        setShouldFinalize(false);
      }
    };

    autoSaveAndFinalize();
  }, [AIOutput, isLoading, shouldFinalize]); // Also watch shouldFinalize state

  return (
    <>
      <OpNoteHeader
        note={currentNote}
        isLoading={isLoading || isSaving}
        saveIsLoading={isSaving}
        refreshResults={(text) => {
          if (currentNote) updateNoteTextWithoutDeepCopy(currentNote, text);
        }}
        selectedCodes={selectedCodes}
        clearSelectedCodes={clearSelectedCodes}
        selectCode={selectCode}
        notesCount={notes?.length ?? 0}
        page={page}
        onChangePage={setPage}
      />

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          gap: {
            xs: '24px',
            sm: '40px',
            lg: '48px',
          },
          flexDirection: {
            xs: 'column',
            md: 'row',
          },
          padding: {
            xs: '0px 16px 0px 16px',
            md: '0px 0px 0px 16px',
            lg: '0px 0px 0px 60px',
          },
          marginTop: `${opNoteHeaderHeight}px`,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            marginBottom: incorrectCodeCombinations ? '100px' : '32px',
          }}
        >
          <CodesSection
            onSaveNote={onSaveNote}
            saveIsLoading={isSaving}
            allCodes={codes}
            selectedCodes={selectedCodes}
            note={currentNote}
            selectCode={selectCode}
            selectedAll={selectedAll}
            selectMultiplier={selectMultiplier}
            selectModifiers={selectModifiers}
            addManualCode={addManualCode}
            flagCode={flagCode}
            isLoading={isLoading}
            hasSelectedAll={
              codes.length !== 0 &&
              codes.filter((c) => !c.selected).length === 0
            }
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            backgroundColor: '#ffffff',
            borderLeft: '1px solid  rgba(82, 86, 98, 0.20)',
            minHeight: {
              xs: '400px',
              md: `calc(100vh - ${opNoteHeaderHeight}px - ${appHeaderHeight}px + 20px)`,
            },
            maxWidth: {
              xs: '100%',
              md: '38%',
              xl: '36%',
            },
            padding: '48px 32px',
            marginBottom: incorrectCodeCombinations ? '76px' : '0px',
          }}
        >
          <AIOutputSection
            icd10={AIOutput?.icd10}
            modifiers={AIOutput?.modifiers}
            units={AIOutput?.units}
            grant={AIOutput?.grants_choice}
          />
        </Box>
      </Box>
      <CCIAlertBottomBar open={incorrectCodeCombinations} />
    </>
  );
};

export default EditOpNoteExtendedSurgery;
